import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateUserDetails } from "../../../../redux/features/authSlice";
import { AiLabEnrolmentContext } from "./AiLab";
import {
  AI_LAB_COURSES,
  calculateEnrolmentDetails
} from "../../constants/ai-lab-constants";
import { AlertTriangle, Lock } from "lucide-react";
import axios from "axios";
import envConfig from "../../../../config/envConfig";
import { collection, addDoc, doc, Timestamp, writeBatch, setDoc, arrayUnion, getDocs, query, where } from "firebase/firestore";
import { db, auth } from "../../../../config/firebaseConfig";

// Modular sub-components
import ChildrenCounter from "./ChildrenCounter";
import ChildDetailsCard from "./ChildDetailsCard";
import ScheduleModal from "./ScheduleModal";
import CartCard from "./CartCard";

const EnrolForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    numChildren,
    setNumChildren,
    childrenData,
    setChildrenData,
    otherQueries,
    setOtherQueries,
    totalAmount,
    enrolmentId,
    setEnrolmentId,
    setPaymentSuccess,
    setPaymentFailed,
    loading,
    setLoading,
    error,
    setError,
    stage,
    setStage,
    parentsDetails,
    parentId,
    pendingEnrolment,
    handleResumePending,
    handleDiscardPending
  } = useContext(AiLabEnrolmentContext);

  const isStudent = parentsDetails?.accountType === "student";

  // Modal active state: null or { childIdx, courseId }
  const [activeModal, setActiveModal] = useState(null);

  // Local state for pre-filling parent details
  const [parentContact, setParentContact] = useState("");
  const [parentEmail, setParentEmail] = useState("");

  useEffect(() => {
    const fetchParentDetailsDirectly = async () => {
      const email = auth?.currentUser?.email || parentsDetails?.email || "";
      let phone = parentsDetails?.phone || "";

      if (!phone && auth?.currentUser?.uid) {
        try {
          const q = query(
            collection(db, "parents"),
            where("uid", "==", auth.currentUser.uid)
          );
          const snap = await getDocs(q);
          if (!snap.empty) {
            const docData = snap.docs[0].data();
            phone = docData.phone || "";
          }
        } catch (err) {
          console.error("Error fetching parent phone directly:", err);
        }
      }

      setParentContact(phone);
      setParentEmail(email);
    };

    fetchParentDetailsDirectly();
  }, [parentsDetails, auth?.currentUser]);

  // Clean error on mount
  useEffect(() => {
    setError("");
  }, [setError]);

  const details = calculateEnrolmentDetails(childrenData);

  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
      document.body.appendChild(script);
    });
  };

  const handleCourseClick = (childIdx, courseId) => {
    setChildrenData((prev) => {
      const copy = [...prev];
      const child = { ...copy[childIdx] };
      const interests = child.interests || [];
      if (!interests.includes(courseId)) {
        child.interests = [...interests, courseId];
      }
      copy[childIdx] = child;
      return copy;
    });
    setActiveModal({ childIdx, courseId });
  };

  const handleSaveSchedule = (childIdx, courseId, day, timeSlot) => {
    setChildrenData((prev) => {
      const copy = [...prev];
      const child = { ...copy[childIdx] };
      const interests = child.interests || [];
      
      if (!interests.includes(courseId)) {
        child.interests = [...interests, courseId];
      }
      
      const schedule = { ...child.schedule };
      schedule[courseId] = { day, timeSlot };
      child.schedule = schedule;
      
      copy[childIdx] = child;
      return copy;
    });
  };

  const handleRemoveCourse = (childIdx, courseId) => {
    setChildrenData((prev) => {
      const copy = [...prev];
      const child = { ...copy[childIdx] };
      
      child.interests = (child.interests || []).filter((id) => id !== courseId);
      
      const schedule = { ...child.schedule };
      delete schedule[courseId];
      child.schedule = schedule;
      
      copy[childIdx] = child;
      return copy;
    });
  };

  const handleChildFieldChange = (childIdx, field, value) => {
    setChildrenData((prev) => {
      const copy = [...prev];
      copy[childIdx] = { ...copy[childIdx], [field]: value };
      return copy;
    });
  };

  // Perform client-side validations
  const validateForm = () => {
    for (let i = 0; i < childrenData.length; i++) {
      const child = childrenData[i];
      if (!child.name?.trim()) {
        setError(`Please enter a name for Child ${i + 1}`);
        return false;
      }
      if (!child.grade) {
        setError(`Please select a class/grade for Child ${i + 1}`);
        return false;
      }
      if (!child.interests || child.interests.length === 0) {
        setError(`Please select at least one course for ${child.name}`);
        return false;
      }

      // Check schedules & overlaps
      const scheduledSlots = new Set();
      for (let j = 0; j < child.interests.length; j++) {
        const courseId = child.interests[j];
        const sched = child.schedule?.[courseId];
        if (!sched?.day || !sched?.timeSlot) {
          setError(`Please select a schedule slot for ${child.name}'s ${AI_LAB_COURSES.find(c => c.id === courseId)?.label} course.`);
          return false;
        }

        const slotKey = `${sched.day}_${sched.timeSlot}`;
        if (scheduledSlots.has(slotKey)) {
          setError(`${child.name} cannot have multiple courses scheduled at the same time (${sched.day} at ${sched.timeSlot}). Please choose a different schedule.`);
          return false;
        }
        scheduledSlots.add(slotKey);
      }
    }
    return true;
  };

  const handleCheckout = async () => {
    setError("");
    if (!validateForm()) return;

    setLoading(true);
    let activeEnrolmentId = enrolmentId;

    try {
      // 1. Create or update Firestore pending enrolment doc(s) in children collection
      setStage("saving_enrolment");
      
      const batch = writeBatch(db);
      
      const updatedChildrenData = childrenData.map(c => {
        let childId = c.id;
        if (!childId) {
          if (parentsDetails?.accountType === "student") {
            childId = parentId;
          } else {
            const newDocRef = doc(collection(db, "students"));
            childId = newDocRef.id;
          }
        }
        return { ...c, id: childId };
      });
      
      setChildrenData(updatedChildrenData);

      updatedChildrenData.forEach(c => {
        const childDocRef = doc(db, "students", c.id);
        const childData = {
          name: c.name,
          school: c.school || "",
          grade: c.grade,
          parentUid: parentsDetails?.accountType === "student" ? null : parentId,
          enrolment: {
            ...c.enrolment,
            "ai-lab": {
              courses: c.interests.reduce((acc, courseId) => {
                acc[courseId] = c.schedule[courseId] || {};
                return acc;
              }, {}),
              paymentStatus: "pending",
              enrolledAt: Timestamp.fromDate(new Date()),
              otherQueries: otherQueries || ""
            }
          }
        };
        batch.set(childDocRef, childData, { merge: true });
      });

      await batch.commit();
      
      activeEnrolmentId = parentId;
      setEnrolmentId(activeEnrolmentId);

      // 2. Load payment script
      setStage("creating_order");
      try {
        await loadRazorpayScript();
      } catch (err) {
        setError("Razorpay failed to load. Please disable ad-blockers and check your connection.");
        setLoading(false);
        return;
      }

      // 3. Initiate Order through PHP API
      const backendUrl = `${envConfig.backendUrl}/paymentV1`;
      const currency = "INR";
      const keyRes = await axios.get(`${backendUrl}?action=getkey`, { timeout: 10000 });
      const key = keyRes.data.key;

      const orderRes = await axios.post(
        `${backendUrl}?action=checkout`,
        { amount: totalAmount, currency },
        { timeout: 12000 }
      );
      const order = orderRes.data.order;

      // 4. Razorpay options configuration
      const options = {
        key,
        order_id: order.id,
        amount: order.amount,
        currency: order.currency,
        name: envConfig.appFullName,
        description: "AI Lab Enrolment",
        image: "/img/appLogo.png",
        prefill: {
          name: auth?.currentUser?.displayName || parentsDetails?.name || "",
          email: parentEmail || auth?.currentUser?.email || "",
          contact: parentContact || auth?.currentUser?.phoneNumber || "",
        },
        notes: {
          enrolmentId: activeEnrolmentId,
          uid: parentId || "",
        },
        theme: { color: "#2563EB" },
        handler: async (response) => {
          try {
            setStage("verifying");
            setLoading(true);

            // Backend payment verification check
            const verificationResponse = await axios.post(
              `${backendUrl}?action=paymentVerification`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                receipt: order?.receipt,
                amount: order?.amount,
                currency: order?.currency,
              },
              { timeout: 12000 }
            );

            if (verificationResponse?.data?.status === "success") {
              setStage("saving");

              const batch = writeBatch(db);

              const paymentRef = doc(db, "payments", response.razorpay_payment_id);
              batch.set(paymentRef, {
                orderId: response.razorpay_order_id,
                amount: order.amount,
                currency: order.currency,
                paymentId: response.razorpay_payment_id,
                paymentSignature: response.razorpay_signature,
                uid: parentId || "",
                userId: parentsDetails?.id || parentId || "",
                status: "verified",
                paymentAt: Timestamp.fromDate(new Date()),
                enrolmentId: activeEnrolmentId,
                type: "ai-lab"
              }, { merge: true });

              updatedChildrenData.forEach(c => {
                const childDocRef = doc(db, "students", c.id);
                batch.update(childDocRef, {
                  "enrolment.ai-lab.paymentStatus": "verified",
                  "enrolment.ai-lab.paymentRef": paymentRef.id,
                  "enrolment.ai-lab.paymentId": response.razorpay_payment_id,
                  "enrolment.ai-lab.paymentAt": Timestamp.fromDate(new Date()),
                });
              });


              await batch.commit();

              // Trigger WhatsApp Enrollment Success Notification
              try {
                const accountType = parentsDetails?.accountType === "student" ? "student" : "parent";
                const isStudentMode = accountType === "student";
                const payload = {
                  accountType,
                  phone: parentContact || parentsDetails?.phone || auth?.currentUser?.phoneNumber || "",
                };

                if (isStudentMode) {
                  payload.studentName = parentsDetails?.name || auth?.currentUser?.displayName || "Student";
                  payload.courses = updatedChildrenData[0].interests.map(courseId => {
                    const courseInfo = AI_LAB_COURSES.find(c => c.id === courseId);
                    const sched = updatedChildrenData[0].schedule?.[courseId] || {};
                    return {
                      name: courseInfo ? courseInfo.label : courseId,
                      day: sched.day || "",
                      timeSlot: sched.timeSlot || ""
                    };
                  });
                } else {
                  payload.parentName = parentsDetails?.name || auth?.currentUser?.displayName || "Parent";
                  payload.children = updatedChildrenData.map(c => ({
                    name: c.name,
                    courses: c.interests.map(courseId => {
                      const courseInfo = AI_LAB_COURSES.find(crs => crs.id === courseId);
                      const sched = c.schedule?.[courseId] || {};
                      return {
                        name: courseInfo ? courseInfo.label : courseId,
                        day: sched.day || "",
                        timeSlot: sched.timeSlot || ""
                      };
                    })
                  }));
                }

                await axios.post(
                  `${envConfig.backendUrl}/sendEnrollmentSuccessWebV1`,
                  payload
                );
              } catch (e) {
                console.error("Failed to send WhatsApp enrolment success message:", e);
              }

              if (parentsDetails?.accountType === "student") {
                const updatedEnrolment = {
                  ...parentsDetails.enrolment,
                  "ai-lab": {
                    courses: updatedChildrenData[0].interests.reduce((acc, courseId) => {
                      acc[courseId] = updatedChildrenData[0].schedule[courseId] || {};
                      return acc;
                    }, {}),
                    paymentStatus: "verified",
                    paymentRef: paymentRef.id,
                    paymentId: response.razorpay_payment_id,
                    paymentAt: new Date().toISOString()
                  }
                };
                dispatch(updateUserDetails({ enrolment: updatedEnrolment }));
              }

              sessionStorage.removeItem("ai-lab-payment-retryData");
              setPaymentSuccess(true);
              setPaymentFailed(false);
            } else {
              throw new Error("Payment verification failed.");
            }
          } catch (err) {
            console.error("Verification error:", err);
            sessionStorage.setItem("ai-lab-payment-retryData", JSON.stringify({ 
              response, 
              order, 
              activeEnrolmentId: activeEnrolmentId,
              childrenData: updatedChildrenData,
              accountType: parentsDetails?.accountType
            }));
            setError("Saved payment details failed. Please retry.");
            setPaymentFailed(true);
          } finally {
            setLoading(false);
            setStage("idle");
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setStage("idle");
            setPaymentFailed(true);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        setError("Transaction failed. Please check your bank status.");
        setLoading(false);
        setPaymentFailed(true);
      });

      setStage("opening_checkout");
      rzp.open();

    } catch (err) {
      console.error("Initiate order payment failed:", err);
      setError("Payment processing failed. Try again.");
      setLoading(false);
      setPaymentFailed(true);
    }
  };

  // Find occupied slots for the child currently being edited in schedule modal
  const getOccupiedSlotsForActiveModal = () => {
    if (!activeModal) return [];
    const { childIdx, courseId } = activeModal;
    const child = childrenData[childIdx];
    const occupied = [];
    
    Object.entries(child.schedule || {}).forEach(([cId, sched]) => {
      if (cId !== courseId && sched.day && sched.timeSlot) {
        const cInfo = AI_LAB_COURSES.find((c) => c.id === cId);
        occupied.push({
          day: sched.day,
          timeSlot: sched.timeSlot,
          courseLabel: cInfo ? cInfo.label : "Other Course"
        });
      }
    });
    return occupied;
  };

  return (
    <div className="space-y-6 text-left max-w-7xl mx-auto">
      {/* Header UI Card (Image 1 style) */}
      <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 flex flex-col items-center text-center space-y-4 shadow-xs">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#E0F2FE] border border-[#BAE6FD] text-[#0369a1] text-xs font-bold">
          <span className="animate-pulse">✨</span> Practido AI LAB Program
        </div>
        
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-950 tracking-tight leading-tight">
          {isStudent ? "Enrol in a Live Online Course" : "Enrol Your Child in a Live Online Course"}
        </h2>
        
        <p className="text-xs sm:text-sm text-gray-400 font-semibold max-w-2xl leading-relaxed">
          {isStudent
            ? "Select your course, complete payment, and we'll confirm your first class within 24 hours - directly on WhatsApp."
            : "Select your course, complete payment, and we'll confirm your child's first class within 24 hours - directly on WhatsApp."}
        </p>
        
        {/* Horizontal highlight row */}
        <div className="w-full bg-[#F5FAFE]/80 border border-[#E0F0FC] rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-around gap-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-white rounded-xl border border-gray-150 shadow-3xs text-blue-500 flex items-center justify-center">
              <Lock className="w-4 h-4" />
            </div>
            <span className="text-xs font-bold text-gray-600">100% Secure via Razorpay</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="p-2 bg-white rounded-xl border border-gray-150 shadow-3xs text-emerald-500 flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-xs font-bold text-gray-600">Instant WhatsApp update</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="p-2 bg-white rounded-xl border border-gray-150 shadow-3xs text-purple-500 flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              </svg>
            </div>
            <span className="text-xs font-bold text-gray-600">Classes 1 to 12 supported</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3.5 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-xs font-extrabold flex gap-2 items-center">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Grid Container */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Left column: Form inputs */}
        <div className="lg:col-span-7 space-y-6">
          {/* Children counter */}
          {parentsDetails?.accountType !== "student" && (
            <ChildrenCounter
              numChildren={numChildren}
              onChangeChildren={setNumChildren}
            />
          )}

          {/* Children detail cards */}
          <div className="space-y-5">
            {childrenData.map((child, idx) => (
              <ChildDetailsCard
                key={idx}
                childIdx={idx}
                child={child}
                isChildUser={parentsDetails?.accountType === "student"}
                onFieldChange={(field, val) => handleChildFieldChange(idx, field, val)}
                onCourseClick={(courseId) => handleCourseClick(idx, courseId)}
              />
            ))}
          </div>

          {/* Queries / Remarks Textarea */}
          <div className="bg-white border border-gray-200 rounded-3xl p-5 sm:p-6 shadow-2xs space-y-2.5">
            <label className="block text-xs font-black text-gray-400 uppercase tracking-wider">
              Any specific queries or requests? (Optional)
            </label>
            <textarea
              placeholder="e.g. Schedule constraints, learning goals, details about prior coding experience, etc."
              value={otherQueries}
              onChange={(e) => setOtherQueries(e.target.value)}
              className="w-full text-xs sm:text-sm border border-gray-300 rounded-2xl px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none min-h-[96px] transition bg-white"
            />
          </div>
        </div>

        {/* Right column: Sticky checkout Summary */}
        <CartCard
          childrenData={childrenData}
          details={details}
          loading={loading}
          stage={stage}
          handleCheckout={handleCheckout}
          isStudent={isStudent}
        />

      </div>

      {/* Schedule picker Popup Modal */}
      {activeModal && (
        <ScheduleModal
          isOpen={!!activeModal}
          onClose={() => setActiveModal(null)}
          course={AI_LAB_COURSES.find(c => c.id === activeModal.courseId)}
          currentSchedule={childrenData[activeModal.childIdx]?.schedule?.[activeModal.courseId]}
          occupiedSlots={getOccupiedSlotsForActiveModal()}
          onSave={(day, timeSlot) => handleSaveSchedule(activeModal.childIdx, activeModal.courseId, day, timeSlot)}
          onRemove={() => handleRemoveCourse(activeModal.childIdx, activeModal.courseId)}
        />
      )}

      {/* Pending Enrolment Resume Popup Modal */}
      {pendingEnrolment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-gray-100 max-w-md w-full p-6 sm:p-8 shadow-2xl relative space-y-6 text-center animate-in fade-in zoom-in-95 duration-200">
            {/* Warning Header Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 text-amber-500 mx-auto">
              <AlertTriangle className="w-8 h-8" />
            </div>

            {/* Title & Subtitle */}
            <div className="space-y-2">
              <h3 className="text-xl sm:text-2xl font-black text-gray-950 tracking-tight">
                Resume draft enrolment?
              </h3>
              <p className="text-xs sm:text-sm text-gray-400 font-semibold leading-relaxed">
                We found a pending enrolment draft on your account. Would you like to resume your previous progress or start a clean new enrolment?
              </p>
            </div>

            {/* Saved Draft Summary Preview */}
            {pendingEnrolment.children && pendingEnrolment.children.length > 0 && (
              <div className="bg-amber-50/50 border border-amber-100 rounded-2xl p-4 text-left space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between items-center text-amber-900 font-bold">
                  <span>Saved Draft Details:</span>
                  <span className="bg-amber-100/60 text-amber-800 px-2 py-0.5 rounded-full text-xs font-black">
                    {pendingEnrolment.children.length} {pendingEnrolment.children.length === 1 ? "Child" : "Children"}
                  </span>
                </div>
                <ul className="space-y-1.5 text-gray-650 font-semibold list-disc list-inside">
                  {pendingEnrolment.children.map((c, i) => (
                    <li key={i}>
                      <span className="font-extrabold text-gray-900">{c.name || `Child ${i + 1}`}</span>
                      {c.grade ? ` (Class ${c.grade})` : ""}
                      {c.interests && c.interests.length > 0 && (
                        <span className="text-gray-400 font-bold">
                          {" "}- {c.interests.map(id => AI_LAB_COURSES.find(course => course.id === id)?.label || id).join(", ")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTAs */}
            <div className="flex flex-col gap-2.5 pt-2">
              <button
                onClick={handleResumePending}
                className="w-full px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs sm:text-sm font-bold shadow-sm transition"
              >
                Resume Draft
              </button>
              <button
                onClick={handleDiscardPending}
                className="w-full px-6 py-3 border border-red-200 text-red-650 hover:bg-red-50 rounded-xl text-xs sm:text-sm font-bold transition"
              >
                Discard & Start Clean
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full px-6 py-2 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-xl text-xs font-bold transition"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrolForm;
